/**
 * Wizard "Créer ma fiche séance" — Sprint 2-A
 *
 * Route : /enseignant/creer-fiche
 *
 * Flux :
 *  1. Choix de la trame (Salle / Conduite)
 *  2. Méta-informations (titre, durée, niveau, lieu, matériel)
 *  3. Remplissage des étapes (champs Content + Items + Notes)
 *  4. Prévisualisation dans <PedagogicalCanvas> + mode présentation + impression
 *  5. Sauvegarde locale automatique (localStorage)
 *
 * Pour rester pragmatique sur le MVP : pas de back-end (multi-fiches par utilisateur
 * via localStorage). Une seule fiche brouillon active à la fois.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Presentation as PresentationIcon,
  Bike,
  Save,
  Trash2,
  Printer,
  Maximize2,
  Eye,
  Sparkles,
  PenSquare,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Clock,
  GraduationCap,
  MapPin,
  Package,
  Plus,
  X,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import PedagogicalCanvas from '../components/teacher/PedagogicalCanvas';
import PresentationModeOverlay from '../components/teacher/PresentationMode';
import { getTrameById, getStepsForTrame } from '../data/teacher/canevasMeta';
import useSeo from '../hooks/useSeo';

const STORAGE_KEY = 'c2r:wizard-fiche-seance:draft';

const EMPTY_DRAFT = {
  trameId: 'conduite',
  title: '',
  kicker: '',
  meta: { duree: '', niveau: '', lieu: '', materiel: '' },
  steps: {},
};

function loadDraft() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data && typeof data === 'object') return { ...EMPTY_DRAFT, ...data };
  } catch {
    // ignore
  }
  return null;
}

function saveDraft(draft) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
  } catch {
    // ignore
  }
}

/* ─── Sous-composant : édition d'une étape ─────────────────────────── */
function StepEditor({ step, value, onChange, accent }) {
  const [open, setOpen] = useState(true);
  const Icon = step.icon;
  const data = value || { content: '', items: [], notes: '' };

  const updateField = (field, val) => {
    onChange({ ...data, [field]: val });
  };

  const addItem = () => updateField('items', [...(data.items || []), '']);
  const updateItem = (idx, val) => {
    const next = [...(data.items || [])];
    next[idx] = val;
    updateField('items', next);
  };
  const removeItem = (idx) => {
    const next = (data.items || []).filter((_, i) => i !== idx);
    updateField('items', next);
  };

  const isFilled = !!data.content || (data.items?.some((it) => it.trim()) ?? false);

  return (
    <section
      className="rounded-xl border border-border bg-card overflow-hidden"
      style={{ borderLeft: `4px solid ${accent}` }}
      data-testid={`editor-${step.id}`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 p-3.5 text-left hover:bg-muted/30 transition-colors"
      >
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center text-white font-black flex-shrink-0 text-sm"
          style={{ background: accent }}
        >
          {step.n}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Icon className="w-4 h-4" style={{ color: accent }} />
            <h3 className="text-sm font-black">{step.label}</h3>
            {isFilled ? (
              <Badge className="text-[9px] font-bold bg-[#16a34a]/15 text-[#16a34a] border-0">
                <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" /> Renseigné
              </Badge>
            ) : (
              <Badge variant="outline" className="text-[9px] font-bold text-muted-foreground">
                À remplir
              </Badge>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground italic mt-0.5">{step.intent}</p>
        </div>
        {open ? (
          <ChevronUp className="w-4 h-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="w-4 h-4 text-muted-foreground" />
        )}
      </button>

      {open && (
        <div className="p-3.5 pt-1 space-y-3 border-t border-border/40 bg-background/40">
          <p className="text-[11px] italic text-muted-foreground">
            <strong className="not-italic">Conseil — </strong>
            {step.description}
          </p>

          {/* Content */}
          <div>
            <Label htmlFor={`${step.id}-content`} className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">
              Contenu principal <span className="text-muted-foreground/60 italic normal-case">(texte libre · HTML simple autorisé : &lt;strong&gt;, &lt;em&gt;)</span>
            </Label>
            <Textarea
              id={`${step.id}-content`}
              value={data.content || ''}
              onChange={(e) => updateField('content', e.target.value)}
              rows={2}
              placeholder={step.examples?.[0] || ''}
              className="mt-1.5 text-sm"
              data-testid={`field-${step.id}-content`}
            />
          </div>

          {/* Items list */}
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">
              Points-clés / Critères / Listes
            </Label>
            <div className="mt-1.5 space-y-1.5">
              {(data.items || []).map((it, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <span
                    className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{ background: accent }}
                  />
                  <Input
                    value={it}
                    onChange={(e) => updateItem(i, e.target.value)}
                    placeholder={step.examples?.[i % (step.examples?.length || 1)] || 'Saisir…'}
                    className="text-sm flex-1"
                    data-testid={`field-${step.id}-item-${i}`}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeItem(i)}
                    className="h-7 w-7 p-0 flex-shrink-0 text-muted-foreground hover:text-destructive"
                    aria-label="Retirer cette ligne"
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addItem}
                className="gap-1.5 mt-1.5"
                data-testid={`add-item-${step.id}`}
              >
                <Plus className="w-3.5 h-3.5" /> Ajouter une ligne
              </Button>
            </div>
          </div>

          {/* Notes moniteur */}
          <div>
            <Label htmlFor={`${step.id}-notes`} className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">
              Note moniteur <span className="text-muted-foreground/60 italic normal-case">(optionnel · point d&apos;attention, rappel)</span>
            </Label>
            <Textarea
              id={`${step.id}-notes`}
              value={data.notes || ''}
              onChange={(e) => updateField('notes', e.target.value)}
              rows={2}
              className="mt-1.5 text-xs italic"
              data-testid={`field-${step.id}-notes`}
            />
          </div>
        </div>
      )}
    </section>
  );
}

/* ─── Page Wizard principale ───────────────────────────────────────── */
export default function CreerFicheSeanceWizard() {
  useSeo({
    title: 'Créer une fiche séance ECSR · Wizard',
    description:
      'Crée ta propre fiche séance ECSR conforme aux trames officielles : choisis ta trame (Salle ou Conduite), remplis les étapes, prévisualise et lance le mode présentation pour la classe.',
    path: '/enseignant/creer-fiche',
  });

  const [draft, setDraft] = useState(() => loadDraft() || EMPTY_DRAFT);
  const [presenting, setPresenting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Persistance auto à chaque modification
  useEffect(() => {
    saveDraft(draft);
  }, [draft]);

  const trame = getTrameById(draft.trameId);
  const trameAccent = trame?.accent || '#cf2031';
  const orderedSteps = getStepsForTrame(draft.trameId);

  const progress = useMemo(() => {
    const filled = orderedSteps.filter((s) => {
      const d = draft.steps[s.id];
      return d && (d.content?.trim() || d.items?.some((it) => it?.trim()));
    });
    return { filled: filled.length, total: orderedSteps.length };
  }, [draft, orderedSteps]);

  const updateMeta = (field, val) =>
    setDraft((d) => ({ ...d, meta: { ...d.meta, [field]: val } }));

  const updateStep = (stepId, val) =>
    setDraft((d) => ({ ...d, steps: { ...d.steps, [stepId]: val } }));

  const switchTrame = (newTrameId) => {
    if (newTrameId === draft.trameId) return;
    // confirme la perte si des données existent
    const hasData =
      draft.title?.trim() || Object.keys(draft.steps || {}).length > 0;
    if (hasData) {
      const ok = window.confirm(
        "Changer de trame va effacer le contenu déjà saisi (la structure des étapes est différente). Continuer ?"
      );
      if (!ok) return;
    }
    setDraft({ ...EMPTY_DRAFT, trameId: newTrameId });
  };

  const handleReset = () => {
    const ok = window.confirm("Vider complètement le brouillon ?");
    if (!ok) return;
    setDraft(EMPTY_DRAFT);
  };

  const handlePrint = () => {
    setShowPreview(true);
    setTimeout(() => window.print(), 200);
  };

  const isReadyForPreview = !!(draft.title?.trim() && progress.filled > 0);

  return (
    <div className="space-y-5" data-testid="wizard-page">
      <Link
        to="/enseignant"
        className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-muted-foreground hover:text-foreground no-print"
        data-testid="back-to-teacher"
      >
        <ArrowLeft className="w-4 h-4" /> Espace Pro
      </Link>

      {/* HEADER (caché à l'impression) */}
      <header
        className="mc-card p-5 sm:p-7 relative overflow-hidden no-print"
        style={{ borderLeft: `6px solid ${trameAccent}` }}
      >
        <div
          className="absolute -top-16 -right-16 w-72 h-72 rounded-full blur-3xl opacity-25 pointer-events-none"
          style={{ background: trameAccent }}
          aria-hidden="true"
        />
        <div className="relative">
          <div
            className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-widest mb-2"
            style={{ background: `${trameAccent}1a`, color: trameAccent }}
          >
            <PenSquare className="w-3 h-3" /> Wizard · Créer ma fiche
          </div>
          <h1
            className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight leading-[1.05]"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Construis ta fiche séance
          </h1>
          <p className="text-sm sm:text-base text-foreground/80 mt-3 max-w-3xl leading-relaxed">
            Choisis ta <strong>trame</strong>, remplis les <strong>étapes officielles</strong>,
            prévisualise et lance le <strong>mode présentation</strong>. Le brouillon est
            sauvegardé automatiquement sur cet appareil — aucun risque de perte.
          </p>
          {/* Barre progression */}
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <div className="flex-1 min-w-[200px] max-w-md">
              <div className="flex items-center justify-between text-[11px] mb-1">
                <span className="font-bold text-muted-foreground">Progression</span>
                <span className="tabular-nums font-black" style={{ color: trameAccent }}>
                  {progress.filled} / {progress.total}
                </span>
              </div>
              <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full transition-all"
                  style={{
                    width: `${(progress.filled / progress.total) * 100}%`,
                    background: trameAccent,
                  }}
                />
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              className="gap-1.5"
              data-testid="btn-reset-draft"
            >
              <Trash2 className="w-3.5 h-3.5" /> Vider
            </Button>
          </div>
        </div>
      </header>

      {/* CHOIX TRAME (caché à l'impression) */}
      <section className="mc-card p-2 sm:p-3 no-print" data-testid="trame-switcher">
        <div className="grid grid-cols-2 gap-1.5">
          <button
            type="button"
            onClick={() => switchTrame('conduite')}
            data-testid="switch-conduite"
            className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 rounded-md text-sm font-bold transition-all ${
              draft.trameId === 'conduite'
                ? 'bg-[#cf2031] text-white shadow-md'
                : 'bg-background hover:bg-muted/60 text-foreground/85'
            }`}
          >
            <Bike className="w-4 h-4" />
            <span className="text-left">
              <span className="block text-[11px] uppercase tracking-wider opacity-80">
                Trame 1 · 4 phases · 9 étapes
              </span>
              <span className="block leading-tight">Leçon de conduite</span>
            </span>
          </button>
          <button
            type="button"
            onClick={() => switchTrame('salle')}
            data-testid="switch-salle"
            className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 rounded-md text-sm font-bold transition-all ${
              draft.trameId === 'salle'
                ? 'bg-[#1d4ed8] text-white shadow-md'
                : 'bg-background hover:bg-muted/60 text-foreground/85'
            }`}
          >
            <PresentationIcon className="w-4 h-4" />
            <span className="text-left">
              <span className="block text-[11px] uppercase tracking-wider opacity-80">
                Trame 2 · 7 étapes
              </span>
              <span className="block leading-tight">Cours en salle</span>
            </span>
          </button>
        </div>
      </section>

      {/* MÉTA-INFOS (caché à l'impression) */}
      <section
        className="mc-card p-4 sm:p-5 no-print"
        data-testid="meta-section"
      >
        <h2 className="text-base sm:text-lg font-black mb-3 flex items-center gap-2">
          <Sparkles className="w-4 h-4" style={{ color: trameAccent }} /> Méta-informations
        </h2>
        <div className="space-y-3">
          <div>
            <Label htmlFor="f-title" className="text-[11px] uppercase tracking-wider font-bold">
              Titre de la séance <span className="text-destructive">*</span>
            </Label>
            <Input
              id="f-title"
              value={draft.title}
              onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
              placeholder={
                draft.trameId === 'salle'
                  ? "Ex : Les distances d'arrêt à moto"
                  : "Ex : Maîtriser la réalisation d'un freinage"
              }
              className="mt-1.5 text-base font-bold"
              data-testid="field-title"
            />
          </div>
          <div>
            <Label htmlFor="f-kicker" className="text-[11px] uppercase tracking-wider font-bold">
              Référence courte <span className="text-muted-foreground/70 italic normal-case">(REMC ou contexte)</span>
            </Label>
            <Input
              id="f-kicker"
              value={draft.kicker}
              onChange={(e) => setDraft((d) => ({ ...d, kicker: e.target.value }))}
              placeholder={
                draft.trameId === 'salle'
                  ? 'Ex : Cours CCS · 1h · Groupe 8 élèves'
                  : 'Ex : C1-g · Plateau · 1h · Individuel'
              }
              className="mt-1.5"
              data-testid="field-kicker"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { key: 'duree', label: 'Durée', icon: Clock, ph: '1 heure' },
              { key: 'niveau', label: 'Niveau / REMC', icon: GraduationCap, ph: 'C1-g · Maniement de la moto' },
              { key: 'lieu', label: 'Lieu', icon: MapPin, ph: 'Plateau · zone freinage 30 m' },
              { key: 'materiel', label: 'Matériel', icon: Package, ph: '2 cônes · décamètre · gilet jaune' },
            ].map((f) => (
              <div key={f.key}>
                <Label className="text-[11px] uppercase tracking-wider font-bold flex items-center gap-1.5">
                  <f.icon className="w-3 h-3" /> {f.label}
                </Label>
                <Input
                  value={draft.meta[f.key] || ''}
                  onChange={(e) => updateMeta(f.key, e.target.value)}
                  placeholder={f.ph}
                  className="mt-1.5 text-sm"
                  data-testid={`field-meta-${f.key}`}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ÉDITEUR ÉTAPES (caché à l'impression) */}
      <section className="space-y-2 no-print" data-testid="steps-section">
        <h2 className="text-base sm:text-lg font-black mb-1 flex items-center gap-2">
          <PenSquare className="w-4 h-4" style={{ color: trameAccent }} />
          Étapes ({orderedSteps.length})
        </h2>
        <p className="text-xs text-muted-foreground italic mb-2">
          Clique sur une étape pour la déplier et la remplir. Le contenu est sauvegardé
          automatiquement.
        </p>
        <div className="space-y-2.5">
          {orderedSteps.map((step) => (
            <StepEditor
              key={step.id}
              step={step}
              value={draft.steps[step.id]}
              onChange={(val) => updateStep(step.id, val)}
              accent={step.color}
            />
          ))}
        </div>
      </section>

      {/* ACTIONS (caché à l'impression) */}
      <section className="mc-card p-4 sm:p-5 no-print" data-testid="actions-section">
        <h2 className="text-base sm:text-lg font-black mb-3 flex items-center gap-2">
          <Save className="w-4 h-4" style={{ color: trameAccent }} /> Utiliser ma fiche
        </h2>
        <p className="text-xs text-muted-foreground mb-3">
          Le brouillon est sauvegardé automatiquement. Pour aller plus loin :
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => setShowPreview((v) => !v)}
            className="gap-1.5"
            style={{ background: trameAccent }}
            disabled={!isReadyForPreview}
            data-testid="btn-toggle-preview"
          >
            <Eye className="w-4 h-4" />
            {showPreview ? 'Masquer la prévisualisation' : 'Prévisualiser la fiche'}
          </Button>
          <Button
            variant="outline"
            onClick={() => setPresenting(true)}
            disabled={!isReadyForPreview}
            className="gap-1.5"
            data-testid="btn-present"
          >
            <Maximize2 className="w-4 h-4" /> Mode présentation
          </Button>
          <Button
            variant="outline"
            onClick={handlePrint}
            disabled={!isReadyForPreview}
            className="gap-1.5"
            data-testid="btn-print"
          >
            <Printer className="w-4 h-4" /> Imprimer / PDF
          </Button>
        </div>
        {!isReadyForPreview && (
          <p className="text-[11px] italic text-muted-foreground mt-2">
            Renseigne au moins un titre et une étape pour activer la prévisualisation.
          </p>
        )}
      </section>

      {/* PRÉVISUALISATION (visible à l'impression aussi) */}
      {(showPreview || isReadyForPreview) && showPreview && (
        <section className="pt-2" data-testid="preview-section">
          <div className="no-print text-[11px] uppercase tracking-wider font-black text-muted-foreground mb-2">
            ↓ Prévisualisation
          </div>
          <PedagogicalCanvas
            trameId={draft.trameId}
            title={draft.title || 'Sans titre'}
            kicker={draft.kicker}
            meta={draft.meta}
            steps={draft.steps}
            testid="wizard-preview"
          />
        </section>
      )}

      {/* Mode présentation overlay */}
      {presenting && (
        <PresentationModeOverlay
          trameId={draft.trameId}
          title={draft.title || 'Sans titre'}
          kicker={draft.kicker}
          meta={draft.meta}
          steps={draft.steps}
          onClose={() => setPresenting(false)}
        />
      )}
    </div>
  );
}
